import { assertNever } from '@eversports/utility-types'

import {
  CustomPaymentMethodType,
  PaymentMethodType,
  PaymentResponse,
  StripeSourceTypeToPaymentMethod,
} from '../../typings/Payment'

import { ProcessPaymentFunction } from '../../data/context'
import { getSharedConfig } from '../../get-config'
import confirmOrder from './confirm-order'
import preparePaymentAndPoll from './prepare-payment-and-poll'
import confirmPaymentSetupAndPoll from './confirm-payment-setup-and-poll'
import confirmPaymentAndPoll from './confirm-payment-and-poll'

const processPayment: ProcessPaymentFunction = async (
  paymentProvider,
  paymentAPI,
  options,
): Promise<PaymentResponse> => {
  if (!paymentProvider.stripe) {
    throw new Error('Stripe was not correctly loaded')
  }

  // add the timestamp of the last payment attempt to be able to
  // - detect payment state transitions to the same state as before (f.e.: FAILED => FAILED)
  const lastPaymentAttempt = new Date()

  const { orderId } = options

  if (!options.elements) {
    throw new Error('Payment function was called without elements')
  }

  const { error: submitError, selectedPaymentMethod: stripeSelectedPaymentMethodType } = await options.elements.submit()

  if (submitError) {
    return { wasSuccessful: false, error: submitError.message }
  }

  const selectedPaymentMethod =
    stripeSelectedPaymentMethodType === getSharedConfig().OFFLINE_PAYMENT_CUSTOM_STRIPE_PAYMENT_METHOD_ID
      ? CustomPaymentMethodType.PAYMENT_METHOD_OFFLINE
      : StripeSourceTypeToPaymentMethod[stripeSelectedPaymentMethodType!]

  switch (selectedPaymentMethod) {
    case CustomPaymentMethodType.PAYMENT_METHOD_OFFLINE:
      return confirmOrder(paymentAPI, orderId)
    case PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE:
      if (!options.payment.canSetupRecurringPaymentMethod) {
        throw new Error('SEPA online payment method can only be used with a recurring payment method')
      }

      if (options.savedPaymentMethodId) {
        return confirmPaymentAndPoll(paymentAPI, { orderId, options, lastPaymentAttempt })
      } else {
        return confirmPaymentSetupAndPoll(paymentProvider, paymentAPI, {
          orderId,
          options,
          lastPaymentAttempt,
        })
      }
    case PaymentMethodType.PAYMENT_METHOD_PAYPAL:
    case PaymentMethodType.PAYMENT_METHOD_BANCONTACT:
    case PaymentMethodType.PAYMENT_METHOD_IDEAL:
    case PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD:
    case PaymentMethodType.PAYMENT_METHOD_GIROPAY:
    case PaymentMethodType.PAYMENT_METHOD_EPS:
    case PaymentMethodType.PAYMENT_METHOD_TWINT:
    case PaymentMethodType.PAYMENT_METHOD_SOFORT: {
      if (options.payment.canSetupRecurringPaymentMethod) {
        return confirmPaymentSetupAndPoll(paymentProvider, paymentAPI, {
          orderId,
          options,
          lastPaymentAttempt,
        })
      } else {
        return preparePaymentAndPoll(paymentProvider, paymentAPI, {
          orderId,
          options,
          lastPaymentAttempt,
          selectedPaymentMethod,
        })
      }
    }
    case PaymentMethodType.PAYMENT_METHOD_MULTIBANCO:
      throw new Error('Multibanco payment method is not supported')
    default:
      return assertNever(selectedPaymentMethod)
  }
}

export default processPayment
